import React, { useState, useEffect, useCallback, useMemo } from "react";
import { AlertTriangle, AlertOctagon, X, ChevronDown, ChevronUp, RotateCw, Check, CheckCheck, Info } from "lucide-react";
import { getCurrentUser } from "../lib/auth.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { Oddelovac } from "./ui.jsx";
import { apiFetch } from "../lib/api.js";
import { formatDateTime } from "../lib/format.js";

// Banner se systémovými chybami pro ownera. Polluje /api/system-errors každých 5 min.
// Cílem je říct uživateli LIDSKY co se děje a co s tím udělat — žádné kódy a stack tracy.
//
// Mapping `source|code` → friendly { title, hint, icon } v ERROR_INFO.
// Pokud chyba není v mapě, fallback na surový message (rozumný default).
const POLL_INTERVAL_MS = 15 * 60_000; // 25.5.2026: 5→15 min, Netlify DB credit optimalizace
const ROLES_S_ALERTY = new Set(["owner", "manager"]);

// Lidské popisky pro známé chyby. Klíč je `source|code`. Hodnota:
//   title — krátký nadpis (max ~60 znaků)
//   hint  — co s tím udělat (max ~150 znaků)
//   parseSubject — funkce extrahuje konkrétní subjekt (jméno klienta, trenéra, atd.) z message/detail
const ERROR_INFO = {
  "trainers|UNKNOWN_TRAINER_IN_CACHE": {
    title: (subject) => subject ? `Trenér „${subject}" není v systému` : "Neznámý trenér v rezervacích",
    hint: (subject, detail) => {
      const m = String(detail || "").match(/\((\d+)×\)/);
      const count = m ? m[1] : "?";
      return `V rezervacích má ${count} tréninků, ale chybí v seznamu trenérů. Doplň ho v HR, Lidé, nebo tam uprav jeho jméno, aby odpovídalo rezervacím. Tréninky se zatím nezapočítávají do výplaty.`;
    },
    parseSubject: (detail) => {
      const m = String(detail || "").match(/Mismatch:\s*"([^"]+)"/);
      return m ? m[1] : "";
    },
  },
  "system-health|OAUTH_ENV_MISSING": {
    title: () => "Chybí přihlašovací údaje pro Google",
    hint: () => "Bez nich aplikace neodešle e-maily (přihlašovací kódy, potvrzení rezervací) a nečte Gmail (pohyby na účtu) ani Google Analytics a Search Console. Zkontroluj v Netlify env vars: GMAIL_CLIENT_ID, GMAIL_CLIENT_SECRET, GMAIL_REFRESH_TOKEN.",
  },
  "system-health|OAUTH_REFRESH_FAIL": {
    title: () => "Vypršelo přihlášení do Googlu",
    hint: () => "Aplikace nemůže číst Gmail (pohyby na účtu) ani Google Analytics a Search Console. Vygeneruj nový refresh token a aktualizuj GMAIL_REFRESH_TOKEN v Netlify env vars.",
  },
  "system-health|OAUTH_FAIL": {
    title: () => "Vypršelo přihlášení do Googlu",
    hint: () => "Aplikace nemůže číst Gmail (pohyby na účtu) ani Google Analytics a Search Console. Vygeneruj nový refresh token a aktualizuj GMAIL_REFRESH_TOKEN v Netlify env vars.",
  },
  "system-health|CRON_STALE": {
    title: (subject) => subject ? `Automatika „${subject}" se zasekla` : "Automatika se zasekla",
    hint: () => "Naplánovaná úloha už dlouho neproběhla. Zkontroluj v Netlify zda je scheduled function aktivní a logy úspěšné.",
    parseSubject: (msg) => {
      const m = String(msg || "").match(/"([^"]+)"/);
      return m ? m[1] : "";
    },
  },
  "system-health|CRON_NO_HEARTBEAT": {
    title: (subject) => subject ? `Automatika „${subject}" ještě nezapsala heartbeat` : "Automatika ještě nezapsala heartbeat",
    hint: () => "Cron buď ještě nikdy neběžel, nebo nemá integrovaný heartbeat. Pokud je nový, počkej na první spuštění.",
    parseSubject: (msg) => {
      const m = String(msg || "").match(/"([^"]+)"/);
      return m ? m[1] : "";
    },
  },
  "cashflow-parse-email|CRON_FAIL": {
    title: () => "Stahování plateb z banky selhalo",
    hint: () => "Cron pro Raiffeisen email parser nestáhl nové platby. Zkontroluj připojení Gmailu nebo formát nových emailů.",
  },
};

// Generic fallback per source — když konkrétní code v ERROR_INFO není.
const SOURCE_FALLBACK = {
  "trainers": "Modul trenéři",
  "trener-rozvrh": "Rozvrh trenéra",
  "trener-rozvrh-pattern": "Generátor rozvrhu",
  "trener-rozvrh-override": "Úpravy slotů v rozvrhu",
  "trener-rozvrh-absence": "Plánované absence",
  "trener-aktivita": "Aktivita trenéra",
  "trener-rutiny": "Rutiny trenéra",
  "trener-ukoly": "Úkoly trenéra",
  "data-overview": "Hlavní dashboard",
  "data-monthly": "Měsíční report",
  "data-weekly": "Týdenní report",
  "data-yearly": "Roční report",
  "data-total": "Lifetime statistiky",
  "clients": "Klienti",
  "dokumenty": "Dokumenty",
  "notifications": "Notifikace",
  "cashflow": "Cashflow",
  "cashflow-parse-pdf": "Cashflow PDF import",
  "cashflow-parse-email": "Cashflow email import",
  "changelog": "Change log",
  "settings": "Nastavení",
  "auth-verify": "Přihlášení",
  "system-errors": "System errors endpoint",
  "system-health": "System health check",
  "frontend": "Aplikace v prohlížeči",
};

// Od odpojení Sheetu (8. 10. 2026) hlásí neznámého trenéra výpočet z databáze.
ERROR_INFO["trainers-pg|UNKNOWN_TRAINER_IN_CACHE_PG"] = ERROR_INFO["trainers|UNKNOWN_TRAINER_IN_CACHE"];

const CODE_GENERIC_HINT = {
  "QUOTA_EXCEEDED": "Externí služba vyčerpala limit požadavků. Pokud se to opakuje, zvyš cache TTL nebo backoff.",
  "OAUTH_FAIL": "Vypršelo přihlášení do Googlu. Aktualizuj GMAIL_REFRESH_TOKEN v Netlify env.",
  "PERMISSION_DENIED": "Aplikace nemá oprávnění k tomuto zdroji. Zkontroluj přístupová práva nebo API klíč.",
  "NETWORK": "Síťová chyba. Zkus později.",
  "TIMEOUT": "Operace se nestihla v limitu. Zkus jednodušší dotaz nebo zvyš timeout.",
  "JS_EXCEPTION": "Něco spadlo v prohlížeči. Otevři Developer Tools pro detail.",
  "HTTP_500": "Server vrátil chybu 500. Detail najdeš ve stack trace.",
  "UNHANDLED": "Nezachycená výjimka. Detail najdeš ve stack trace.",
  "FRONTEND_ERROR": "Něco spadlo v prohlížeči.",
};

// Vyrobí lidské { title, hint } pro daný error
function humanize(err) {
  const key = `${err.source}|${err.code}`;
  const info = ERROR_INFO[key];
  if (info) {
    const subject = info.parseSubject ? (info.parseSubject(err.detail) || info.parseSubject(err.message)) : "";
    return {
      title: info.title(subject),
      hint: info.hint(subject, err.detail),
      hasMapping: true,
    };
  }
  // Fallback — pokud máme skutečnou chybovou zprávu, použij ji jako titulek (NE generic placeholder).
  // Generic hint ze CODE_GENERIC_HINT slouží jako sekundární vysvětlení (co s tím).
  const sourceLabel = SOURCE_FALLBACK[err.source] || err.source;
  const realMessage = String(err.message || "").trim();
  const genericHint = CODE_GENERIC_HINT[err.code] || "";
  if (realMessage) {
    // Skutečná chyba je nejvíc informativní
    return {
      title: `${sourceLabel}: ${realMessage.substring(0, 120)}`,
      hint: genericHint || "Detail chyby najdeš v technickém info.",
      hasMapping: false,
    };
  }
  // Bez message — fallback na code + generic
  return {
    title: `${sourceLabel}${err.code ? `, ${err.code}` : ""}`,
    hint: genericHint || "Detail chyby najdeš v technickém info.",
    hasMapping: false,
  };
}

// Severity → barva + ikona
const SEVERITY_STYLE = {
  critical: { Icon: AlertOctagon, bg: "bg-red-600", text: "text-white", chip: "bg-red-100 text-red-800 border-red-300", label: "Kritické" },
  error:    { Icon: AlertTriangle, bg: "bg-orange-500", text: "text-white", chip: "bg-orange-100 text-orange-800 border-orange-300", label: "Chyba" },
  warn:     { Icon: AlertTriangle, bg: "bg-amber-100", text: "text-amber-900", chip: "bg-amber-100 text-amber-800 border-amber-300", label: "Varování" },
  info:     { Icon: Info, bg: "bg-sky-100", text: "text-sky-900", chip: "bg-sky-100 text-sky-800 border-sky-300", label: "Info" },
};

export default function SystemAlertBanner() {
  const user = getCurrentUser();
  const role = user?.role || "";
  const enabled = ROLES_S_ALERTY.has(role);

  const [errors, setErrors] = useState([]);
  const [counts, setCounts] = useState({ total: 0, critical: 0, error: 0, warn: 0, info: 0 });
  const [expanded, setExpanded] = useState(false);
  const [confirmResolveAll, setConfirmResolveAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissedUntil, setDismissedUntil] = useState(0);
  const [techDetail, setTechDetail] = useState({}); // rowNum → bool (tech detail rozbalený)

  const refresh = useCallback(async (force = false) => {
    try {
      const url = force ? "/api/system-errors?stav=aktivní&force=1" : "/api/system-errors?stav=aktivní";
      const r = await apiFetch(url);
      if (!r.ok) return;
      const j = await r.json();
      setErrors(j.errors || []);
      setCounts(j.counts || { total: 0, critical: 0, error: 0, warn: 0, info: 0 });
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [enabled, refresh]);

  // Group errors by source+code+message — stejné chyby do 1 řádku s počtem.
  // Backend nově dedupuje při zápisu, ale starší řádky můžou být stále duplicitní → sjednocujeme i ve frontend.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of errors) {
      const key = `${e.source}|${e.code}|${e.message}`;
      if (!map.has(key)) {
        map.set(key, { ...e, count: 1, rowNums: [e.rowNum], pgIds: [e.pgId || 0] });
      } else {
        const g = map.get(key);
        g.count++;
        g.rowNums.push(e.rowNum);
        g.pgIds.push(e.pgId || 0);
        // Použij nejnovější timestamp
        if (e.timestamp > g.timestamp) g.timestamp = e.timestamp;
      }
    }
    return [...map.values()].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [errors]);

  const resolveGroup = async (group) => {
    if (busy) return;
    setBusy(true);
    setErrors((prev) => prev.filter((e) => !group.rowNums.includes(e.rowNum) && !group.pgIds.includes(e.pgId)));
    try {
      // Fix 26.5.2026: resolveGroup nyni sbira i pgIds (PG fallback pro orphan rows
      // bez Sheet zaznamu, ktere vznikly behem incidentu Sheet write fail).
      // Backend automaticky pouzije Sheet+PG resolve pokud rowNum>=2, jinak PG-only via pgId.
      const promises = [];
      // Sheet+PG resolve (rowNum >= 2)
      for (const rn of group.rowNums) {
        if (rn >= 2) {
          promises.push(apiFetch("/api/system-errors", {
            method: "POST",
            body: JSON.stringify({ action: "resolve", rowNum: rn }),
          }));
        }
      }
      // PG-only resolve (orphan rows)
      for (const pid of group.pgIds) {
        if (pid > 0) {
          promises.push(apiFetch("/api/system-errors", {
            method: "POST",
            body: JSON.stringify({ action: "resolve", pgId: pid }),
          }));
        }
      }
      await Promise.all(promises);
      // Force refresh aby cache na backendu (5s TTL na Lambda instances) nepřežil
      // a uživatel okamžitě uvidí že entries jsou vyřešené napříč všemi Lambdami.
      refresh(true);
    } catch { refresh(true); } finally { setBusy(false); }
  };

  const resolveAll = async () => {
    if (busy) return;
    setBusy(true);
    setErrors([]);
    try {
      await apiFetch("/api/system-errors", {
        method: "POST",
        body: JSON.stringify({ action: "resolveAll" }),
      });
      refresh();
    } catch { refresh(); } finally { setBusy(false); }
  };

  if (!enabled) return null;
  // Banner zobrazuje JEN critical chyby. Vše ostatní je v zvonečku /notifikace
  // (sjednocený feed s byznys notifikacemi). Tím se zabrání duplicitě UI.
  if (counts.critical === 0) return null;
  const isDismissed = Date.now() < dismissedUntil;
  if (isDismissed) return null;

  const hasCritical = counts.critical > 0;
  const hasError = counts.error > 0;
  const palette = hasCritical
    ? { bg: "bg-red-600", border: "border-red-700", text: "text-white", btnBg: "bg-red-700/80 hover:bg-red-800", icon: AlertOctagon }
    : hasError
    ? { bg: "bg-orange-500", border: "border-orange-600", text: "text-white", btnBg: "bg-orange-600/80 hover:bg-orange-700", icon: AlertTriangle }
    : { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-900", btnBg: "bg-amber-200 hover:bg-amber-300", icon: AlertTriangle };
  const Icon = palette.icon;

  // Lidský headline — počet UNIKÁTNÍCH problémů (po groupingu), ne řádků
  const uniqueCount = grouped.length;
  const headline = (() => {
    if (hasCritical) return `${counts.critical} kritická situace v automatizaci`;
    if (hasError) {
      return uniqueCount === 1 ? "Jedna chyba v automatizaci" : `${uniqueCount} ${uniqueCount < 5 ? "problémy" : "problémů"} v automatizaci`;
    }
    return uniqueCount === 1 ? "Jedno upozornění" : `${uniqueCount} ${uniqueCount < 5 ? "upozornění" : "upozornění"}`;
  })();

  return (
    <div className={`${palette.bg} ${palette.border} ${palette.text} border-b shadow-md`}>
      {/* Header s headlinem a tlačítky */}
      <div className="px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <Icon className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold">{headline}</div>
          {!expanded && grouped.length > 0 && (() => {
            const first = humanize(grouped[0]);
            return <div className="text-xs opacity-90 truncate">{first.title}</div>;
          })()}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setExpanded((e) => !e)}
            className={`text-xs font-semibold px-3 py-1.5 rounded ${palette.btnBg} inline-flex items-center gap-1`}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? "Sbalit" : "Zobrazit"}
          </button>
          <button onClick={refresh} className={`p-1.5 rounded ${palette.btnBg}`} title="Aktualizovat" aria-label="Aktualizovat">
            <RotateCw className="w-3.5 h-3.5" />
          </button>
          {!hasCritical && (
            <button
              onClick={() => setDismissedUntil(Date.now() + 60 * 60 * 1000)}
              className={`p-1.5 rounded ${palette.btnBg}`}
              title="Skrýt na 1 hodinu"
              aria-label="Zavřít"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Rozbalený seznam */}
      {expanded && (
        <div className="bg-white text-slate-800 max-h-[60vh] overflow-y-auto border-t">
          <div className="px-4 sm:px-6 py-2 flex items-center justify-between bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
            <div className="text-xs text-slate-600">
              {uniqueCount === 1 ? "1 problém" : `${uniqueCount} problémů`}
              {grouped.some((g) => g.count > 1) && (
                <span className="text-slate-400">, stejné chyby jsou sloučené do jednoho řádku</span>
              )}
            </div>
            <button
              onClick={() => !busy && setConfirmResolveAll(true)}
              disabled={busy}
              className="text-xs font-semibold px-3 py-1.5 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Vyřešit vše
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {grouped.map((g) => {
              const human = humanize(g);
              const sev = SEVERITY_STYLE[g.severity] || SEVERITY_STYLE.error;
              const showTech = !!techDetail[g.rowNum];
              return (
                <div key={g.rowNum} className="px-4 sm:px-6 py-3 hover:bg-slate-50">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${sev.chip} border`}>
                      <sev.Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap mb-0.5">
                        <div className="text-sm font-bold text-slate-900">{human.title}</div>
                        {g.count > 1 && (
                          <span className="text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                            {g.count}× za sebou
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-600 leading-snug">{human.hint}</div>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
                        <span>{formatDateTime(g.timestamp)}</span>
                        <button
                          onClick={() => setTechDetail((s) => ({ ...s, [g.rowNum]: !s[g.rowNum] }))}
                          className="hover:text-slate-600 inline-flex items-center gap-0.5 underline-offset-2 hover:underline"
                        >
                          {showTech ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          Technické info
                        </button>
                      </div>
                      {showTech && (
                        <div className="mt-2 p-2.5 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                          {/* 14. 9. 2026 (fáze 6): flex obal, jinak byl oddělovač neviditelný a texty slité */}
                          <div className="inline-flex flex-wrap items-center gap-1.5 text-[11px] font-mono">
                            <span><span className="text-slate-500">Source:</span> <span className="text-slate-800">{g.source}</span></span>
                            <Oddelovac />
                            <span><span className="text-slate-500">Code:</span> <span className="text-slate-800">{g.code}</span></span>
                          </div>
                          <div className="text-[11px] font-mono text-slate-700">
                            <span className="text-slate-500">Zpráva:</span> {g.message}
                          </div>
                          {g.detail && (
                            <pre className="text-[11px] font-mono text-slate-600 whitespace-pre-wrap break-all bg-white p-2 rounded border border-slate-200 max-h-32 overflow-y-auto">{g.detail}</pre>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => resolveGroup(g)}
                      disabled={busy}
                      className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50 flex-shrink-0 inline-flex items-center gap-1"
                      title={g.count > 1 ? `Označit všech ${g.count} výskytů jako vyřešené` : "Označit jako vyřešené"}
                    >
                      <Check className="w-3.5 h-3.5" /> Vyřešeno
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirmResolveAll}
        title="Vyřešit všechny chyby?"
        description={<>Označí se všech <strong>{counts.total}</strong> chyb jako vyřešené. Banner i zvoneček se vyčistí.</>}
        confirmLabel="Označit vše jako vyřešené"
        variant="warning"
        onConfirm={() => { setConfirmResolveAll(false); resolveAll(); }}
        onCancel={() => setConfirmResolveAll(false)}
      />
    </div>
  );
}
